import { patchState, signalStore, withState } from '@ngrx/signals';
import { withDevtools } from '@pc/store';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable } from '@angular/core';
import { isEmpty } from '@pc/shared';
import { QuoteResponse } from '../../shared/models/quote/basic-quote-details';
import { ProcessSource } from '../../shared/models/agent/agent-models';

export interface AgentState {
  source: ProcessSource;
  agentSaleNumberDetails: { id: string | null | undefined; isAgent: boolean };
  organizationId: string | null;
  siteUserId: string | null;
  affiliateId: string | null;
  affiliateName: string | null;
  shouldRedirectOrganization: boolean;
  isAgentExpired: boolean;
  agentQuoteId: string | null;
  organizationName?: string;
  entityName: string | null;
  apiError: HttpErrorResponse | null;
  isAgentDisclaimerOpen: boolean;
  isSpecialDiscountOrganization: boolean;
  isLoading: boolean;
  disclaimerLoader: boolean;
  basicQuoteDetailsId: string | null;
  leadId: string | null;
  quoteId: string | null;
  quoteUrlData: QuoteResponse | null;
  brokerFee: number;
  disclaimerText: string;
  showHeaderBanner: boolean;
}

const agentInitialState: AgentState = {
  source: ProcessSource.Direct,
  agentSaleNumberDetails: { id: null, isAgent: false },
  organizationId: null,
  siteUserId: null,
  affiliateId: null,
  affiliateName: null,
  entityName: null,
  shouldRedirectOrganization: false,
  isAgentExpired: false,
  agentQuoteId: null,
  apiError: null,
  isAgentDisclaimerOpen: false,
  isSpecialDiscountOrganization: false,
  isLoading: false,
  disclaimerLoader: false,
  basicQuoteDetailsId: null,
  leadId: null,
  quoteId: null,
  quoteUrlData: null,
  brokerFee: 0,
  disclaimerText: '',
  showHeaderBanner: false
};

@Injectable({ providedIn: 'root' })
export class ProcessTypeStore extends signalStore(
  { providedIn: 'root' },
  withDevtools('ProcessTypeStore'),
  withState(agentInitialState)
) {
  public readonly isAgentSavedQuoteProcess = computed(() => {
    return (
      this.agentSaleNumberDetails()?.isAgent &&
      !isEmpty(this.agentSaleNumberDetails()?.id) &&
      !isEmpty(this.agentQuoteId())
    );
  });

  public isAgentProcess = computed(() => {
    return this.agentSaleNumberDetails()?.isAgent && !isEmpty(this.agentSaleNumberDetails()?.id);
  });

  public override showHeaderBanner = computed(() => {
    return this.isAgentProcess() || !!this.affiliateId() || !!this.entityName();
  });

  public isFullFlowProcess = computed(() => {
    return this.source() === ProcessSource.Direct || this.source() === ProcessSource.EmailQuote;
  });

  public fillProcessDataFromMainPath(
    affiliate: string | undefined,
    agent: string | undefined,
    organization: string | undefined,
    siteUser: string | undefined,
    basicQuoteDetailsId: string | null,
    quoteId: string | null
  ): void {
    this.setAffiliateId(affiliate ?? '');
    this.setOrganizationId(organization ?? '');
    this.setSiteUserId(siteUser ?? '');
    this.setAgentSaleNumberDetails({ id: agent, isAgent: !isEmpty(agent) });
    this.setIsAgentDisclaimerOpen(!isEmpty(agent));
    this.setBasicQuoteDetailsId(basicQuoteDetailsId);
    this.setQuoteId(quoteId || '');
    this.setIsLoading(false);
  }

  public setSource(source: ProcessSource): void {
    patchState(this, { source });
  }

  public setEntityName(entityName: string): void {
    patchState(this, { entityName });
  }

  public setAgentSaleNumberDetails(agentSaleNumberDetails: { id: string | null | undefined; isAgent: boolean }): void {
    patchState(this, { agentSaleNumberDetails });
  }

  public setIsAgentExpired(isAgentExpired: boolean): void {
    patchState(this, { isAgentExpired });
  }

  public setIsAgentDisclaimerOpen(isAgentDisclaimerOpen: boolean): void {
    patchState(this, { isAgentDisclaimerOpen });
  }

  public setIsLoading(isLoading: boolean): void {
    patchState(this, { isLoading });
  }

  public setApiError(apiError: HttpErrorResponse): void {
    patchState(this, { apiError });
  }

  public setAffiliateName(affiliateName: string): void {
    patchState(this, { affiliateName });
  }

  public setAffiliateId(affiliateId: string): void {
    patchState(this, { affiliateId });
  }

  public setOrganizationName(organizationName: string): void {
    patchState(this, { organizationName });
  }

  public setDisclaimer(disclaimerText: string): void {
    patchState(this, { disclaimerText });
  }

  public setShouldRedirectOrganization(shouldRedirectOrganization: boolean): void {
    patchState(this, { shouldRedirectOrganization });
  }

  public setAgentQuoteId(agentQuoteId: string): void {
    patchState(this, { agentQuoteId });
  }

  public setOrganizationId(organizationId: string | null): void {
    patchState(this, { organizationId });
  }

  public setSiteUserId(siteUserId: string | null): void {
    patchState(this, { siteUserId });
  }

  public setBasicQuoteDetailsId(basicQuoteDetailsId: string | null): void {
    patchState(this, { basicQuoteDetailsId });
  }

  public setLeadId(leadId: string | null): void {
    patchState(this, { leadId });
  }

  public setQuoteId(quoteId: string | null): void {
    patchState(this, { quoteId });
  }

  public setQuoteUrlData(quoteUrlData: QuoteResponse | null): void {
    patchState(this, { quoteUrlData });
  }

  public setBrokerFee(brokerFee: number): void {
    patchState(this, { brokerFee });
  }
}
